import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileImage, FileText, Image, Trash2, X, Zap, TrendingUp, Calendar } from "lucide-react";
import { api, resolveMediaUrl, API_BASE } from "@/lib/api";
import { track } from "@/lib/analytics";

function DownloadMenu({ historyId, filename }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handle = (format) => {
    setOpen(false);
    track("render_downloaded", { format, source: "history" });
    const token = localStorage.getItem("neivai_token");
    const url = `${API_BASE}/history/${historyId}/download?format=${format}`;
    // Use a hidden <a> with the auth token passed as a query param isn't ideal,
    // so we fetch with the Bearer header and trigger a blob download instead.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${filename}.${format}`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download failed. Please try again."));
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition"
      >
        <Download className="h-4 w-4" />
        Download
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-xl overflow-hidden z-10">
          <button onClick={() => handle("jpeg")} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#1A1C1E] hover:bg-[#F3F1EC] transition">
            <FileImage className="h-4 w-4 text-[#C86B53]" /> JPEG
          </button>
          <button onClick={() => handle("png")} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#1A1C1E] hover:bg-[#F3F1EC] transition">
            <Image className="h-4 w-4 text-[#C86B53]" /> PNG
          </button>
          <button onClick={() => handle("pdf")} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#1A1C1E] hover:bg-[#F3F1EC] transition">
            <FileText className="h-4 w-4 text-[#C86B53]" /> PDF
          </button>
        </div>
      )}
    </div>
  );
}

export default function History() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [usage, setUsage] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState("all");
  const [user, setUser] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = () => api.get("/history").then((r) => setItems(r.data)).catch(() => {});
  
  const loadUsage = () => {
    api
      .get("/history/salesperson/usage")
      .then((r) => setUsage(r.data))
      .catch(() => {});
  };

  const loadUser = () => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    loadUsage();
    loadUser();
  }, []);

  const totalCreditsSpent = items.length; // Each render costs 1 credit
  const totalRenders = items.length;
  const creditsLeft = (user?.credits ?? 0);

  const filteredItems = filterSalesperson === "all" 
    ? items 
    : filterSalesperson === "retailer"
    ? items.filter((r) => !r.salesperson_id)
    : items.filter((r) => r.salesperson_id === filterSalesperson);

  // Apply date filter
  const dateFilteredItems = filteredItems.filter((r) => {
    const renderDate = new Date(r.created_at).toISOString().split('T')[0];
    if (startDate && renderDate < startDate) return false;
    if (endDate && renderDate > endDate) return false;
    return true;
  });

  const salespersonOptions = [
    { id: "all", name: "All" },
    { id: "retailer", name: "Retailer" },
    ...usage.map((s) => ({ id: s._id, name: s.salesperson_name || "Unknown" })),
  ];

  const remove = async (id) => {
    if (!window.confirm("Delete this render?")) return;
    await api.delete(`/history/${id}`);
    toast.success("Deleted");
    load();
  };

  const openModal = (r, idx = 0) => { setSelected(r); setActiveIdx(idx); };

  return (
    <div className="space-y-8" data-testid="history-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <span className="overline">Renders</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">History</h1>
          <p className="mt-2 text-[#5C5F62]">Every AI visualization created from your catalog.</p>
        </div>

        {/* Filter Section - Top Right */}
        <div className="flex flex-col gap-3 md:text-right">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <label className="text-sm font-medium text-[#5C5F62]">Filter by:</label>
            <select
              value={filterSalesperson}
              onChange={(e) => setFilterSalesperson(e.target.value)}
              className="px-4 py-2 rounded-md border border-[#E5E5E5] text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]"
            >
              {salespersonOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Calendar className="h-4 w-4 shrink-0 text-[#8A8D91]" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="min-w-0 flex-1 px-3 py-2 rounded-md border border-[#E5E5E5] text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] sm:flex-initial"
              placeholder="dd/mm/yyyy"
            />
            <span className="text-[#8A8D91]">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="min-w-0 flex-1 px-3 py-2 rounded-md border border-[#E5E5E5] text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] sm:flex-initial"
              placeholder="dd/mm/yyyy"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                className="px-3 py-2 text-xs font-medium text-[#C86B53] hover:text-[#B85A42] transition"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Credit Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="neivai-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#8A8D91]">Credits Spent</p>
              <p className="mt-2 text-4xl font-display font-semibold">{totalCreditsSpent}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-[#FEF3F2] flex items-center justify-center">
              <Zap className="h-6 w-6 text-[#B74F4F]" />
            </div>
          </div>
        </div>

        <div className="neivai-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#8A8D91]">Credits Left</p>
              <p className="mt-2 text-4xl font-display font-semibold">{creditsLeft}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-[#E7F3EA] flex items-center justify-center">
              <Zap className="h-6 w-6 text-[#2E7D46]" />
            </div>
          </div>
        </div>

        <div className="neivai-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#8A8D91]">Total Renders</p>
              <p className="mt-2 text-4xl font-display font-semibold">{totalRenders}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-[#F3F1EC] flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-[#C86B53]" />
            </div>
          </div>
        </div>
      </div>



      {filteredItems.length === 0 ? (
        <div className="neivai-card p-12 text-center text-[#8A8D91]">No renders yet.</div>
      ) : dateFilteredItems.length === 0 ? (
        <div className="neivai-card p-12 text-center text-[#8A8D91]">No renders found for the selected date range.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {dateFilteredItems.map((r) => {
            const salesperson = usage.find((s) => s._id === r.salesperson_id);
            return (
              <div key={r.id} className="neivai-card overflow-hidden group" data-testid={`render-${r.id}`}>
                <button onClick={() => openModal(r, 0)} className="block w-full aspect-square bg-[#F3F1EC] overflow-hidden">
                  {r.output_urls?.[0] ? (
                    <img src={resolveMediaUrl(r.output_urls[0])} alt="render" className="h-full w-full object-cover group-hover:scale-105 transition" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xs text-[#8A8D91]">{r.status}</div>
                  )}
                </button>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#8A8D91]">{new Date(r.created_at).toLocaleDateString()}</p>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      {r.output_urls?.[0] && (
                        <button onClick={() => openModal(r, 0)} className="p-1.5 rounded hover:bg-[#F3F1EC]">
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-[#F3F1EC] text-[#B74F4F]">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {r.salesperson_id ? (
                    <p className="text-xs font-medium text-[#5C5F62]">
                      {salesperson?.salesperson_name || "Unknown"}
                    </p>
                  ) : (
                    <p className="text-xs font-medium text-[#C86B53]">Retailer</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col" onClick={() => setSelected(null)}>
          {/* top bar */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0" onClick={(e) => e.stopPropagation()}>
            <p className="text-white/70 text-sm truncate max-w-xs">
              {selected.prompt || "—"}
            </p>
            <div className="flex items-center gap-3">
              {selected.output_urls?.[activeIdx] && (
                <DownloadMenu
                  historyId={selected.id}
                  filename={`neiv-render-${selected.id}-${activeIdx + 1}`}
                />
              )}
              <button onClick={() => setSelected(null)} className="text-white p-2 rounded hover:bg-white/10">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* main image */}
          <div className="flex-1 flex items-center justify-center overflow-auto px-4" onClick={(e) => e.stopPropagation()}>
            {selected.output_urls?.[activeIdx] && (
              <img
                src={resolveMediaUrl(selected.output_urls[activeIdx])}
                alt="render"
                className="max-h-[75vh] max-w-full object-contain rounded-lg"
              />
            )}
          </div>

          {/* thumbnails (if multiple outputs) */}
          {selected.output_urls?.length > 1 && (
            <div className="flex justify-center gap-2 pb-4 shrink-0" onClick={(e) => e.stopPropagation()}>
              {selected.output_urls.map((u, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  className={`h-14 w-14 rounded overflow-hidden border-2 transition ${i === activeIdx ? "border-[#C86B53]" : "border-transparent opacity-60 hover:opacity-100"}`}
                >
                  <img src={resolveMediaUrl(u)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
